import * as React from 'react';
import { RunwayResponseType } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { tryUpdateAppSettingsV2 } from '../../actions/appSettings';
import { AppSettingsV2Updatable } from '../../actions/updatable';

import { AppComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { AutomationSettings } from './AutomationSettings';
import { antMagenta, runwayColors, tagStyle } from '../styles/globalStyles';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import { useAppSettings, useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { RootState } from '@/reducers/reducers';
import { selectApp, selectAppSettingAutomations } from '@/selectors/selectors';
import { WorkflowUpdatable } from '@/actions/workflow';
import { AutomationSettingsContext } from '@/helpers/automationHelpers';

interface AppAutomationSettingsProps extends AppComponentProps {
    open: boolean;
    automationId: string;
    onAutomationSelected: (automationId: string) => void;
    onClose: () => void;
    onAfterClose: () => void;
    releaseId?: string;
    context?: AutomationSettingsContext;
}

export const AppAutomationSettings: React.FC<AppAutomationSettingsProps> = (props) => {
    const dispatch = useDispatch();

    const appSettings = useAppSettings({ appId: props.appId, orgId: props.orgId });
    let automations = useSelector((state: RootState) => {
        return selectAppSettingAutomations(state, props.appId);
    });

    const automation =
        appSettings.automations.find((automation) => automation.id === props.automationId) ??
        appSettings.automations.find((automation) => automation.type === props.automationId);

    const workflowId = useSelector((state: RootState) => selectApp(state, props.appId)?.workflowId);

    const [
        updateAutomationSettings,
        updateAutomationSettingsIsLoading,
        updateAutomationSettingsResponseType,
        updateAutomationSettingsError,
    ] = useCallUpdatable(
        WorkflowUpdatable.UpdateAutomationSettings,
        {
            orgId: props.orgId,
            workflowId,
            automationId: automation.id,
        },
        automation.id,
    );

    const sameTypeAutomations = React.useMemo(() => {
        return automations.filter((a) => a.type === automation.type);
    }, [automations]);

    const updateReleaseStepAutomationEnabledIsLoading = useIsLoading(
        AppSettingsV2Updatable.UpdateAutomationSettings,
        automation?.type,
    );
    const updateReleaseStepAutomationSuccess = useUpdated(
        AppSettingsV2Updatable.UpdateAutomationSettings,
        automation?.type,
    );
    const updateReleaseStepAutomationEnabledError = useError(
        AppSettingsV2Updatable.UpdateAutomationSettings,
        automation?.type,
    );

    const updateAutomationEnabledIsLoading =
        automation.stepConfigId ? updateAutomationSettingsIsLoading : updateReleaseStepAutomationEnabledIsLoading;

    const updateAutomationEnabledSuccess =
        automation.stepConfigId ?
            updateAutomationSettingsResponseType === RunwayResponseType.Success
        :   updateReleaseStepAutomationSuccess;
    const updateAutomationEnabledError =
        automation.stepConfigId ? updateAutomationSettingsError : updateReleaseStepAutomationEnabledError;

    React.useEffect(() => {
        if (updateAutomationEnabledSuccess) {
            globalMessage.success('Updated automation settings!');

            props.onClose();
        }
    }, [updateAutomationEnabledSuccess]);

    React.useEffect(() => {
        if (updateAutomationEnabledError) {
            globalMessage.error(updateAutomationEnabledError);
        }
    }, [updateAutomationEnabledError]);

    return (
        automation && (
            <AutomationSettings
                {...props}
                onAutomationSelected={props.onAutomationSelected}
                sameTypeAutomations={sameTypeAutomations}
                automation={automation}
                isLoading={updateAutomationEnabledIsLoading}
                onSubmit={(request) => {
                    if (automation.stepConfigId) {
                        updateAutomationSettings(request);
                    } else {
                        dispatch(
                            tryUpdateAppSettingsV2(
                                AppSettingsV2Updatable.UpdateAutomationSettings,
                                { ...request, releaseId: props.releaseId },
                                automation?.type,
                            ),
                        );
                    }
                }}
            />
        )
    );
};

export const BetaBadge: React.FC = () => {
    return (
        <Tooltip
            overlayStyle={{
                maxWidth: 208,
            }}
            title={
                <Text
                    style={{
                        color: runwayColors.white,
                    }}
                >
                    This feature is in beta. If you experience any unexpected behavior, please{' '}
                    <Link href={`#hs-chat-open`}>get in touch</Link>
                </Text>
            }
        >
            <Tag
                color="white"
                style={{
                    ...tagStyle,
                    fontSize: 10,
                    borderColor: antMagenta.magenta4,
                    color: antMagenta.magenta4,
                }}
            >
                Beta
            </Tag>
        </Tooltip>
    );
};
